import React, { useCallback, useEffect, useState } from "react";
import {
    FiCalendar,
    FiCheck,
    FiChevronLeft,
    FiChevronRight,
    FiEye,
    FiLoader,
    FiSearch,
    FiTag,
    FiX,
} from "react-icons/fi";
import { MdCheckCircle } from "react-icons/md";
import axiosInstance from "../../utils/api";
import Sidebar from "../../components/Sidebar";

const TABS = [
    { value: "pending", label: "Pending" },
    { value: "", label: "All" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
];

const ORG_LABELS = {
    event_agency: "Event agency",
    association: "Association",
    company: "Company",
    public_sector: "Public sector",
    trade_show_organizer: "Trade show / congress organizer",
    other: "Other",
};

const STATUS_STYLES = {
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rejected: "bg-red-50 text-red-700 border-red-200",
};

const PERMISSION_ICONS = {
    retroplanning: FiCalendar,
    cleaning: MdCheckCircle,
    hse: MdCheckCircle,
    badging: FiTag,
};

const LIMIT = 15;

const StatusBadge = ({ status }) => (
    <span
        className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-semibold capitalize ${STATUS_STYLES[status]}`}
    >
        {status}
    </span>
);

const formatDate = (d) =>
    new Date(d).toLocaleString(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });

const Modal = ({ title, onClose, children }) => (
    <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
        onClick={onClose}
    >
        <div
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
        >
            <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-900">{title}</h3>
                <button
                    onClick={onClose}
                    className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                >
                    <FiX size={18} />
                </button>
            </div>
            {children}
        </div>
    </div>
);

const Detail = ({ label, value }) => (
    <div>
        <dt className="text-xs font-semibold uppercase tracking-wide text-gray-400">
            {label}
        </dt>
        <dd className="mt-0.5 text-sm text-gray-900 break-words">{value || "—"}</dd>
    </div>
);

const inputCls =
    "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20";

const DemoRequests = () => {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [pages, setPages] = useState(1);
    const [page, setPage] = useState(1);
    const [status, setStatus] = useState("pending");
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);

    // modal state: { type: "view" | "approve" | "reject", item }
    const [modal, setModal] = useState(null);
    const [acting, setActing] = useState(false);
    const [actionError, setActionError] = useState(null);

    // approve form
    const [features, setFeatures] = useState([]);
    const [featuresLoading, setFeaturesLoading] = useState(false);
    const [permissions, setPermissions] = useState([]);
    const [eventQuota, setEventQuota] = useState({ mode: "single", limit: 1 });
    const [standsQuota, setStandsQuota] = useState({ mode: "unlimited", limit: 100 });
    // reject form
    const [reason, setReason] = useState("");

    // debounce search
    useEffect(() => {
        const t = setTimeout(() => {
            setSearch(searchInput.trim());
            setPage(1);
        }, 400);
        return () => clearTimeout(t);
    }, [searchInput]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const { data } = await axiosInstance.get("/demo-requests", {
                params: { status: status || undefined, search: search || undefined, page, limit: LIMIT },
            });
            setItems(data.items);
            setTotal(data.total);
            setPages(data.pages || 1);
        } catch (err) {
            setError(err?.response?.data?.message || "Failed to load demo requests.");
        } finally {
            setLoading(false);
        }
    }, [status, search, page]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        if (!notice) return;
        const t = setTimeout(() => setNotice(null), 4000);
        return () => clearTimeout(t);
    }, [notice]);

    const loadFeatures = async () => {
        setFeaturesLoading(true);
        try {
            const { data } = await axiosInstance.get("/billing/pricing");
            setFeatures(data);
        } catch (err) {
            setActionError(err?.response?.data?.message || "Failed to load permissions.");
        } finally {
            setFeaturesLoading(false);
        }
    };

    const openModal = (type, item) => {
        setActionError(null);
        setReason("");
        setPermissions([]);
        setEventQuota({ mode: "single", limit: 1 });
        setStandsQuota({ mode: "unlimited", limit: 100 });
        setModal({ type, item });
        if (type === "approve" && features.length === 0) loadFeatures();
    };

    const closeModal = () => !acting && setModal(null);

    const togglePermission = (key) =>
        setPermissions((prev) =>
            prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
        );

    const handleApprove = async () => {
        if (permissions.length === 0) {
            setActionError("Select at least one permission.");
            return;
        }
        setActing(true);
        setActionError(null);
        try {
            const payload = {
                permissions,
                eventQuota: {
                    mode: eventQuota.mode,
                    limit: eventQuota.mode === "multiple" ? Number(eventQuota.limit) || 1 : 1,
                },
                standsQuota: {
                    mode: standsQuota.mode,
                    limit: standsQuota.mode === "limited" ? Number(standsQuota.limit) || 1 : null,
                },
            };
            await axiosInstance.patch(`/demo-requests/${modal.item._id}/approve`, payload);
            setNotice({ type: "success", text: `Approved. Admin account created for ${modal.item.email}.` });
            setModal(null);
            fetchData();
        } catch (err) {
            setActionError(err?.response?.data?.message || "Approval failed.");
        } finally {
            setActing(false);
        }
    };

    const handleReject = async () => {
        setActing(true);
        setActionError(null);
        try {
            const { data } = await axiosInstance.patch(`/demo-requests/${modal.item._id}/reject`, {
                reason: reason.trim() || undefined,
            });
            setNotice({
                type: "success",
                text:
                    data?.emailSent === false
                        ? `Request rejected, but the email to ${modal.item.email} failed to send.`
                        : `Request rejected. ${modal.item.email} has been notified.`,
            });
            setModal(null);
            fetchData();
        } catch (err) {
            setActionError(err?.response?.data?.message || "Rejection failed.");
        } finally {
            setActing(false);
        }
    };

    return (
        <div className="flex min-h-screen bg-gray-50">
            <Sidebar />

            <main className="min-w-0 flex-1 p-6">
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-gray-900">Demo requests</h1>
                    <p className="mt-1 text-sm text-gray-500">
                        Review incoming requests. Approving one creates an admin account and emails the credentials;
                        rejecting one emails the applicant.
                    </p>
                </div>

                {notice && (
                    <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700">
                        {notice.text}
                    </div>
                )}

                {/* Toolbar */}
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex gap-1 rounded-xl bg-gray-100 p-1">
                        {TABS.map((t) => (
                            <button
                                key={t.label}
                                onClick={() => {
                                    setStatus(t.value);
                                    setPage(1);
                                }}
                                className={`rounded-lg px-3.5 py-1.5 text-sm font-semibold transition ${status === t.value
                                        ? "bg-white text-gray-900 shadow-sm"
                                        : "text-gray-500 hover:text-gray-800"
                                    }`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>

                    <div className="relative w-full sm:w-72">
                        <FiSearch size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder="Search name, email, company…"
                            className={`${inputCls} pl-9`}
                        />
                    </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
                    <table className="w-full min-w-[760px] text-left text-sm">
                        <thead className="border-b border-[#0B1F3A] bg-[#0B1F3A] text-xs uppercase tracking-wide text-white/80">
                            <tr>
                                <th className="px-4 py-3">Contact</th>
                                <th className="px-4 py-3">Company</th>
                                <th className="px-4 py-3">Type</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3">Received</th>
                                <th className="px-4 py-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {loading ? (
                                <tr>
                                    <td colSpan={6} className="px-4 py-12 text-center text-gray-400">
                                        <FiLoader className="mx-auto animate-spin" size={20} />
                                    </td>
                                </tr>
                            ) : error ? (
                                <tr>
                                    <td colSpan={6} className="px-4 py-12 text-center text-red-600">
                                        {error}{" "}
                                        <button onClick={fetchData} className="font-semibold underline">
                                            Retry
                                        </button>
                                    </td>
                                </tr>
                            ) : items.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-4 py-12 text-center text-gray-400">
                                        No demo requests found.
                                    </td>
                                </tr>
                            ) : (
                                items.map((r) => (
                                    <tr key={r._id} className="hover:bg-gray-50/60">
                                        <td className="px-4 py-3">
                                            <div className="font-semibold text-gray-900">{r.name}</div>
                                            <div className="text-xs text-gray-500">{r.email}</div>
                                            <div className="text-xs text-gray-400">{r.phone}</div>
                                        </td>
                                        <td className="px-4 py-3 text-gray-700">{r.company || "—"}</td>
                                        <td className="px-4 py-3 text-gray-700">{ORG_LABELS[r.orgType] || r.orgType}</td>
                                        <td className="px-4 py-3">
                                            <StatusBadge status={r.status} />
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-gray-500">{formatDate(r.createdAt)}</td>
                                        <td className="px-4 py-3">
                                            <div className="flex justify-end gap-1.5">
                                                <button
                                                    onClick={() => openModal("view", r)}
                                                    title="View"
                                                    className="rounded-lg border border-gray-200 p-2 text-gray-600 hover:bg-gray-50"
                                                >
                                                    <FiEye size={15} />
                                                </button>
                                                {r.status === "pending" && (
                                                    <>
                                                        <button
                                                            onClick={() => openModal("approve", r)}
                                                            className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-700"
                                                        >
                                                            <FiCheck size={14} /> Approve
                                                        </button>
                                                        <button
                                                            onClick={() => openModal("reject", r)}
                                                            className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50"
                                                        >
                                                            <FiX size={14} /> Reject
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
                    <span>{total} request{total === 1 ? "" : "s"}</span>
                    <div className="flex items-center gap-2">
                        <button
                            disabled={page <= 1}
                            onClick={() => setPage((p) => p - 1)}
                            className="rounded-lg border border-gray-200 p-2 hover:bg-gray-50 disabled:opacity-40"
                        >
                            <FiChevronLeft size={16} />
                        </button>
                        <span>
                            Page {page} / {pages}
                        </span>
                        <button
                            disabled={page >= pages}
                            onClick={() => setPage((p) => p + 1)}
                            className="rounded-lg border border-gray-200 p-2 hover:bg-gray-50 disabled:opacity-40"
                        >
                            <FiChevronRight size={16} />
                        </button>
                    </div>
                </div>

                {/* View modal */}
                {modal?.type === "view" && (
                    <Modal title="Demo request" onClose={closeModal}>
                        <dl className="grid grid-cols-2 gap-4">
                            <Detail label="Name" value={modal.item.name} />
                            <Detail label="Email" value={modal.item.email} />
                            <Detail label="Phone" value={modal.item.phone} />
                            <Detail label="Type" value={ORG_LABELS[modal.item.orgType]} />
                            <Detail label="Company" value={modal.item.company} />
                            <Detail label="Received" value={formatDate(modal.item.createdAt)} />
                            <div className="col-span-2">
                                <Detail label="Project" value={modal.item.message} />
                            </div>
                            <div>
                                <dt className="text-xs font-semibold uppercase tracking-wide text-gray-400">Status</dt>
                                <dd className="mt-1">
                                    <StatusBadge status={modal.item.status} />
                                </dd>
                            </div>
                            {modal.item.reviewedBy && (
                                <Detail
                                    label="Reviewed by"
                                    value={`${modal.item.reviewedBy.name} · ${formatDate(modal.item.reviewedAt)}`}
                                />
                            )}
                            {modal.item.rejectionReason && (
                                <div className="col-span-2">
                                    <Detail label="Rejection message" value={modal.item.rejectionReason} />
                                </div>
                            )}
                        </dl>
                    </Modal>
                )}

                {/* Approve modal */}
                {modal?.type === "approve" && (
                    <Modal title="Approve demo request" onClose={closeModal}>
                        <p className="text-sm text-gray-600">
                            An <b>admin</b> account will be created for{" "}
                            <b>{modal.item.name}</b> ({modal.item.email}) with the permissions you select, and the login details will be emailed to them.
                        </p>

                        <div className="mt-5 space-y-4">
                            <div>
                                <label className="mb-1 block text-xs font-bold text-gray-700">Permissions</label>
                                {featuresLoading ? (
                                    <FiLoader className="animate-spin text-gray-400" size={18} />
                                ) : (
                                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                        {features.map((f) => {
                                            const Icon = PERMISSION_ICONS[f.key] || MdCheckCircle;
                                            const active = permissions.includes(f.key);
                                            return (
                                                <button
                                                    type="button"
                                                    key={f.key}
                                                    onClick={() => togglePermission(f.key)}
                                                    className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-left text-sm transition ${active
                                                            ? "border-[#0B1F3A] bg-[#0B1F3A] text-white"
                                                            : "border-gray-200 text-gray-700 hover:bg-gray-50"
                                                        }`}
                                                >
                                                    <Icon size={15} className="mt-0.5 shrink-0" />
                                                    <span>
                                                        <span className="block font-medium">{f.name}</span>
                                                        {f.description && (
                                                            <span className={`block text-xs ${active ? "text-white/70" : "text-gray-400"}`}>
                                                                {f.description}
                                                            </span>
                                                        )}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            <div>
                                <label className="mb-1 block text-xs font-bold text-gray-700">Events quota</label>
                                <div className="flex gap-2">
                                    <select
                                        className={inputCls}
                                        value={eventQuota.mode}
                                        onChange={(e) => setEventQuota({ ...eventQuota, mode: e.target.value })}
                                    >
                                        <option value="single">Single event</option>
                                        <option value="multiple">Multiple events</option>
                                    </select>
                                    {eventQuota.mode === "multiple" && (
                                        <input
                                            type="number"
                                            min={1}
                                            className={`${inputCls} w-28`}
                                            value={eventQuota.limit}
                                            onChange={(e) => setEventQuota({ ...eventQuota, limit: e.target.value })}
                                        />
                                    )}
                                </div>
                            </div>

                            <div>
                                <label className="mb-1 block text-xs font-bold text-gray-700">Stands quota</label>
                                <div className="flex gap-2">
                                    <select
                                        className={inputCls}
                                        value={standsQuota.mode}
                                        onChange={(e) => setStandsQuota({ ...standsQuota, mode: e.target.value })}
                                    >
                                        <option value="unlimited">Unlimited</option>
                                        <option value="limited">Limited</option>
                                    </select>
                                    {standsQuota.mode === "limited" && (
                                        <input
                                            type="number"
                                            min={1}
                                            className={`${inputCls} w-28`}
                                            value={standsQuota.limit}
                                            onChange={(e) => setStandsQuota({ ...standsQuota, limit: e.target.value })}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>

                        {actionError && (
                            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
                                {actionError}
                            </div>
                        )}

                        <div className="mt-6 flex justify-end gap-2">
                            <button
                                onClick={closeModal}
                                disabled={acting}
                                className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleApprove}
                                disabled={acting}
                                className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                            >
                                {acting && <FiLoader size={14} className="animate-spin" />}
                                Approve & create admin
                            </button>
                        </div>
                    </Modal>
                )}

                {/* Reject modal */}
                {modal?.type === "reject" && (
                    <Modal title="Reject demo request" onClose={closeModal}>
                        <p className="text-sm text-gray-600">
                            Reject the request from <b>{modal.item.name}</b> ({modal.item.email})? They will be notified by email.
                        </p>
                        <div className="mt-4">
                            <label className="mb-1 block text-xs font-bold text-gray-700">
                                Message <span className="font-normal text-gray-400">(optional, sent to the applicant by email)</span>
                            </label>
                            <textarea
                                rows={3}
                                className={`${inputCls} resize-none`}
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                            />
                        </div>

                        {actionError && (
                            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
                                {actionError}
                            </div>
                        )}

                        <div className="mt-6 flex justify-end gap-2">
                            <button
                                onClick={closeModal}
                                disabled={acting}
                                className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleReject}
                                disabled={acting}
                                className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
                            >
                                {acting && <FiLoader size={14} className="animate-spin" />}
                                Reject
                            </button>
                        </div>
                    </Modal>
                )}
            </main>
        </div>
    );
};

export default DemoRequests;